import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LOGOS, BRAND, INTERIOR_IMAGES } from "../data/content";
import { useBooking } from "./BookingModal";

const EASE = [0.7, 0, 0.2, 1];

const LineMask = ({ children, delay = 0, className = "" }) => (
  <span className={`mask-line ${className}`}>
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, ease: EASE, delay }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const { openBooking } = useBooking();

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-black"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={INTERIOR_IMAGES[1]}
          alt=""
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 spotlight" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
      </motion.div>

      {/* Top meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 1 }}
        className="absolute inset-x-0 top-[90px] z-10 flex justify-between px-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)] md:px-12"
      >
        <span>Est. Patiala · Punjab</span>
        <span className="hidden md:inline">IAO Accredited</span>
        <span>N 30.33° · E 76.38°</span>
      </motion.div>

      {/* Center content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 flex h-full flex-col justify-between gap-14 px-6 pb-14 pt-40 md:gap-8 md:px-12 md:pb-10 md:pt-44"
      >
        <div className="max-w-[1400px]">
          <div className="mb-8">
            <LineMask delay={0.2}>
              <span className="chip">
                <span className="dot" />
                99 Institute of Beauty & Wellness
              </span>
            </LineMask>
          </div>

          <h1 className="font-serif text-[13vw] font-light leading-[0.85] tracking-[-0.03em] md:text-[10vw] lg:text-[9.5vw]">
            <LineMask delay={0.35}>Mastering</LineMask>
            <LineMask delay={0.5}>
              <span className="italic-serif text-[color:var(--primary)]">
                the art
              </span>{" "}
              of
            </LineMask>
            <LineMask delay={0.65}>
              elegance<span className="blink-caret text-[color:var(--primary)]">.</span>
            </LineMask>
          </h1>
        </div>

        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="max-w-md text-sm leading-relaxed text-[color:var(--muted)] md:text-base"
          >
            A salon and academy in the heart of Patiala. Where senior artists,
            professional-grade formulations, and an IAO-accredited curriculum
            live under one roof — obsessed with the same idea. Craft you can
            feel.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex w-full flex-col items-start gap-4 md:w-auto md:items-end"
          >
            <button
              data-testid="hero-book-btn"
              onClick={openBooking}
              className="group inline-flex w-full items-center justify-center gap-3 border border-[color:var(--primary)] bg-[color:var(--primary)]/10 px-6 py-4 transition hover:bg-[color:var(--primary)] md:w-auto md:justify-start md:gap-4"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
                Start the journey
              </span>
              <span className="font-serif text-base italic md:text-lg">
                Click to book →
              </span>
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)]">
              Salon hrs · {BRAND.timings}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue — desktop only */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[color:var(--muted)]">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown size={14} className="text-[color:var(--primary)]" />
        </motion.div>
      </motion.div>

      {/* Corner numeric */}
      <div className="pointer-events-none absolute bottom-6 right-6 z-10 hidden font-mono text-[10px] uppercase tracking-[0.3em] text-[color:var(--muted)] md:right-12 md:block">
        01 / Signature
      </div>
    </section>
  );
};

export default Hero;
